<script setup lang="ts">
import type { SystemInfoDTO } from '@/types/admin'

import { toAppColor } from '@/composables/useAppColor'

definePage({
  meta: {
    requiresAuth: true,
    requiresAdmin: true,
  },
})

const { setPageTitle } = useBreadcrumbs()
const { d } = useI18n()

// State
const isLoading = ref(false)
const systemInfo = ref<SystemInfoDTO | null>(null)
const error = ref<string | null>(null)

// Frontend version info
const frontendInfo = computed(() => ({
  vueVersion: __VUE_VERSION__,
  nuxtUiVersion: __NUXT_UI_VERSION__,
  viteVersion: __VITE_VERSION__,
  typescriptVersion: __TYPESCRIPT_VERSION__,
  tailwindcssVersion: __TAILWINDCSS_VERSION__,
  buildDate: __APP_VERSION_DATE__,
  version: __APP_VERSION__,
  commitHash: __COMMIT_HASH__,
}))

// Methods
async function fetchSystemInfo() {
  isLoading.value = true
  error.value = null
  try {
    const response = await axios.get<SystemInfoDTO>('/api/admin/system-info')
    systemInfo.value = response.data
  } catch (err) {
    console.error('Error fetching system info:', err)
    error.value = 'Failed to load system information'
  } finally {
    isLoading.value = false
  }
}

// Lifecycle
onMounted(() => {
  setPageTitle('System Information')
  fetchSystemInfo()
})
</script>

<template>
  <div class="space-y-6">
    <!-- Header -->
    <div class="flex items-center justify-between">
      <div>
        <h1 class="text-2xl font-bold">System Information</h1>
        <p class="text-muted">View version and environment details</p>
      </div>
      <UButton
        icon="i-lucide-refresh-cw"
        label="Refresh"
        :loading="isLoading"
        @click="fetchSystemInfo"
        variant="outline"
      />
    </div>

    <!-- Error Message -->
    <AppAlert v-if="error" color="error" :closable="false">
      {{ error }}
    </AppAlert>

    <!-- Backend Information -->
    <UCard>
      <template #header>
        <div class="flex items-center gap-2">
          <UIcon name="i-lucide-server" />
          <span>Backend Information</span>
        </div>
      </template>
      <template #default>
        <div v-if="isLoading" class="space-y-4">
          <USkeleton height="2rem" />
          <USkeleton height="2rem" />
          <USkeleton height="2rem" />
          <USkeleton height="2rem" />
        </div>
        <div v-else-if="systemInfo" class="space-y-4">
          <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <label class="text-sm font-medium text-muted"> .NET Version </label>
              <p class="text-lg font-semibold">
                {{ systemInfo.dotNetVersion }}
              </p>
            </div>
            <div>
              <label class="text-sm font-medium text-muted"> Server Version </label>
              <p class="text-lg font-semibold">
                {{ systemInfo.serverVersion }}
              </p>
            </div>
            <div>
              <label class="text-sm font-medium text-muted"> Build Date </label>
              <p class="text-lg font-semibold">
                {{ d(new Date(systemInfo.buildDate), 'datetimefull') }}
              </p>
            </div>
            <div>
              <label class="text-sm font-medium text-muted"> Environment </label>
              <p class="text-lg font-semibold">
                <UBadge
                  :color="toAppColor(systemInfo.environment === 'Production' ? 'danger' : 'info')"
                >
                  {{ systemInfo.environment }}
                </UBadge>
              </p>
            </div>
          </div>
        </div>
      </template>
    </UCard>

    <!-- Operating System Information -->
    <UCard>
      <template #header>
        <div class="flex items-center gap-2">
          <UIcon name="i-lucide-shield" />
          <span>Operating System</span>
        </div>
      </template>
      <template #default>
        <div v-if="isLoading" class="space-y-4">
          <USkeleton height="2rem" />
          <USkeleton height="2rem" />
          <USkeleton height="2rem" />
        </div>
        <div v-else-if="systemInfo" class="space-y-4">
          <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <label class="text-sm font-medium text-muted"> OS Description </label>
              <p class="text-lg font-semibold">
                {{ systemInfo.osDescription }}
              </p>
            </div>
            <div>
              <label class="text-sm font-medium text-muted"> OS Architecture </label>
              <p class="text-lg font-semibold">
                {{ systemInfo.osArchitecture }}
              </p>
            </div>
            <div>
              <label class="text-sm font-medium text-muted"> Process Architecture </label>
              <p class="text-lg font-semibold">
                {{ systemInfo.processArchitecture }}
              </p>
            </div>
            <div>
              <label class="text-sm font-medium text-muted"> Container Environment </label>
              <p class="text-lg font-semibold">
                <UBadge :color="toAppColor(systemInfo.isContainer ? 'info' : 'secondary')">
                  <UIcon
                    :name="systemInfo.isContainer ? 'i-lucide-box' : 'i-lucide-monitor'"
                    class="mr-2"
                  />
                  {{ systemInfo.isContainer ? 'Containerized' : 'Native' }}
                </UBadge>
              </p>
            </div>
          </div>
        </div>
      </template>
    </UCard>

    <!-- Frontend Information -->
    <UCard>
      <template #header>
        <div class="flex items-center gap-2">
          <UIcon name="i-lucide-monitor" />
          <span>Frontend Information</span>
        </div>
      </template>
      <template #default>
        <div class="space-y-4">
          <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <label class="text-sm font-medium text-muted"> Vue.js Version </label>
              <p class="text-lg font-semibold">
                {{ frontendInfo.vueVersion }}
              </p>
            </div>
            <div>
              <label class="text-sm font-medium text-muted"> Nuxt UI Version </label>
              <p class="text-lg font-semibold">
                {{ frontendInfo.nuxtUiVersion }}
              </p>
            </div>
            <div>
              <label class="text-sm font-medium text-muted"> App Version </label>
              <p class="text-lg font-semibold">
                {{ frontendInfo.version }}
              </p>
            </div>
            <div>
              <label class="text-sm font-medium text-muted"> Build Date </label>
              <p class="text-lg font-semibold">
                {{ d(new Date(frontendInfo.buildDate), 'datetimefull') }}
              </p>
            </div>
            <div>
              <label class="text-sm font-medium text-muted"> Commit Hash </label>
              <p class="font-mono text-lg text-sm font-semibold">
                {{ frontendInfo.commitHash }}
              </p>
            </div>
          </div>
        </div>
      </template>
    </UCard>

    <!-- Build Tools -->
    <UCard>
      <template #header>
        <div class="flex items-center gap-2">
          <UIcon name="i-lucide-wrench" />
          <span>Build Tools & Dependencies</span>
        </div>
      </template>
      <template #default>
        <div class="space-y-4">
          <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <label class="text-sm font-medium text-muted"> Build System </label>
              <p class="text-lg font-semibold">Vite {{ frontendInfo.viteVersion }}</p>
            </div>
            <div>
              <label class="text-sm font-medium text-muted"> TypeScript </label>
              <p class="text-lg font-semibold">
                {{ frontendInfo.typescriptVersion }}
              </p>
            </div>
            <div>
              <label class="text-sm font-medium text-muted"> Tailwind CSS </label>
              <p class="text-lg font-semibold">
                {{ frontendInfo.tailwindcssVersion }}
              </p>
            </div>
            <div>
              <label class="text-sm font-medium text-muted"> Package Manager </label>
              <p class="text-lg font-semibold">npm</p>
            </div>
          </div>
        </div>
      </template>
    </UCard>
  </div>
</template>
